import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMatkaBetPlacement } from "./useMatkaBetPlacement";
import { useDispatch, useSelector } from "react-redux";
import { openLoginModal, openSignupModal } from "../../../features/slices/ModalSlice";
import { fetchMatkaSuggestions } from "../../../api/services/matka";
import { QUICK_MARKETS } from "./constants";
import MatkaFooter from "./MatkaFooter";
import MatkaSessionSelect, {
  applySessionToBids,
  getSessionOptions,
} from "./MatkaSessionSelect";

export default function MatkaPanelGroup({
  selectedMarket,
  selectedGameType,
  onBackToGameTypes,
  onBackToMarkets,
  onQuickMarketSelect,
  onSelectTab,
  onPlaceBet,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const [gameType, setGameType] = useState(() =>
    getSessionOptions(selectedGameType?.betDirection)[0].toLowerCase()
  );
  const [enterPoints, setEnterPoints] = useState("");
  const [panna, setPanna] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [bids, setBids] = useState([]);
  const [notice, setNotice] = useState("");
  const { openReview, betModals } = useMatkaBetPlacement({
    market: selectedMarket,
    gameType: selectedGameType,
    session: gameType,
    bids,
    onPlaced: () => setBids([]),
  });
  const requestId = useRef(0);

  const pointsValue = Number(enterPoints);
  const canAddBid =
    enterPoints.trim() !== "" && Number.isFinite(pointsValue) && pointsValue > 0;
  const totalBids = bids.length;
  const totalPoints = useMemo(
    () => bids.reduce((sum, bid) => sum + (Number(bid.points) || 0), 0),
    [bids]
  );

  useEffect(() => {
    if (panna.length !== 3) {
      requestId.current += 1;
      setSuggestions([]);
      return undefined;
    }

    const digits = panna;
    const id = requestId.current + 1;
    requestId.current = id;
    const timer = setTimeout(() => {
      fetchMatkaSuggestions("panel_group", digits)
        .then((data) => {
          if (id !== requestId.current) return;
          setSuggestions(Array.isArray(data?.panas) ? data.panas.map(String) : []);
        })
        .catch(() => {
          if (id !== requestId.current) return;
          setSuggestions([]);
        });
    }, 300);

    return () => clearTimeout(timer);
  }, [panna]);

  const handleSelectSuggestion = (value) => {
    if (!suggestions.includes(value)) return;
    if (!canAddBid) {
      setNotice("Enter points before adding a suggestion.");
      return;
    }

    setBids((currentBids) => {
      const existingBid = currentBids.find(
        (bid) => bid.panna === value && bid.gameType === gameType
      );
      if (existingBid) {
        const updated = {
          ...existingBid,
          points: Number(existingBid.points) + pointsValue,
        };
        return [updated, ...currentBids.filter((bid) => bid.id !== existingBid.id)];
      }
      return [
        {
          id: Date.now(),
          panna: value,
          gameType,
          points: pointsValue,
        },
        ...currentBids,
      ];
    });
  };

  const handleGameTypeChange = (nextGameType) => {
    setGameType(nextGameType);
    setBids((currentBids) =>
      applySessionToBids(currentBids, nextGameType, (bid) => bid.panna)
    );
  };

  const handleDeleteBid = (bidId) => {
    setBids((currentBids) => currentBids.filter((bid) => bid.id !== bidId));
  };

  const handleContinue = () => {
    if (!totalBids) return;
    if (!user?.userLoggedIn) {
      dispatch(openLoginModal("login"));
      return;
    }
    openReview();
  };

  return (
    <div className="matka-betting-screen-wrapper">
      {/* Top Header */}
      <header className="matka-betting-header">
        <div className="container-fluid custom-padding d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
          {/* Brand Logo & ONLINE badge */}
          <div className="d-flex align-items-center gap-2">
            <span
              className="matka-betting-brand-text"
              onClick={onBackToMarkets}
            >
              Matka
            </span>
            <span className="matka-online-badge">ONLINE</span>
          </div>

          {/* Quick Market Switcher */}
          <div className="matka-quick-market-switcher d-flex align-items-center">
            {QUICK_MARKETS.map((qm, idx) => (
              <React.Fragment key={qm.id}>
                <button
                  type="button"
                  className={`matka-quick-market-btn ${
                    selectedMarket?.id === qm.id ? "active" : ""
                  }`}
                  onClick={() => onQuickMarketSelect(qm.id)}
                >
                  {qm.icon && <span className="me-1">{qm.icon}</span>}
                  {qm.name}
                </button>
                {idx < QUICK_MARKETS.length - 1 && (
                  <span className="matka-switcher-dot">•</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* User Auth Buttons */}
          <div className="d-flex align-items-center gap-2">
            {!user?.userLoggedIn ? (
              <>
                <button
                  type="button"
                  className="matka-betting-login-btn"
                  onClick={() => dispatch(openLoginModal("login"))}
                >
                  LOGIN
                </button>
                <button
                  type="button"
                  className="matka-betting-signup-btn"
                  onClick={() => dispatch(openSignupModal())}
                >
                  SIGN UP
                </button>
              </>
            ) : (
              <span className="matka-user-logged-badge">
                {user?.user?.name || user?.user?.username || "Player"}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Main Body */}
      <main className="matka-betting-body pt-3 pb-4">
        <div className="matka-main-centered-container">
          {/* Back to Market Link */}
          <button
            type="button"
            className="matka-back-to-market-link d-inline-flex align-items-center mb-2"
            onClick={onBackToGameTypes}
          >
            <i className="bi bi-chevron-left me-1"></i>
            Back to{" "}
            {selectedMarket?.name
              ? selectedMarket.name.charAt(0) +
                selectedMarket.name.slice(1).toLowerCase()
              : "Mahadevi"}
          </button>

          {/* Screen Title */}
          <h1 className="matka-panna-bulk-title mb-4">Panel Group</h1>

          {/* Form: Row 1 - GAME TYPE and ENTER POINTS */}
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-6">
              <label className="matka-panna-bulk-label">GAME TYPE</label>
              <MatkaSessionSelect
                  betDirection={selectedGameType?.betDirection}
                  value={gameType}
                  onChange={handleGameTypeChange}
                  variant="bulk"
                />
            </div>

            <div className="col-12 col-md-6">
              <label className="matka-panna-bulk-label">ENTER POINTS</label>
              <input
                type="text"
                inputMode="numeric"
                className="matka-panna-bulk-input"
                placeholder="Enter Points (e.g. 100)"
                value={enterPoints}
                onChange={(e) => setEnterPoints(e.target.value.replace(/\D/g, "").slice(0, 5))}
              />
            </div>
          </div>

          {/* Form: Row 2 - PANNA (Full Width) */}
          <div className="mb-3">
            <label className="matka-panna-bulk-label">PANNA</label>
            <div className="matka-sangam-field">
              <input
                type="text"
                inputMode="numeric"
                className="matka-panna-bulk-input"
                placeholder="985"
                value={panna}
                onChange={(e) => setPanna(e.target.value.replace(/\D/g, "").slice(0, 3))}
              />
              {suggestions.length > 0 && (
                <div className="matka-sangam-suggestions is-inline" role="listbox">
                  {suggestions.map((value) => (
                    <button
                      key={value}
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleSelectSuggestion(value)}
                    >
                      {value}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Current Bids List Card */}
          <div className="matka-bids-list-card mb-4">
            <h3 className="matka-bids-list-title">Current Bids List</h3>
            <div className="table-responsive">
              <table className="matka-bids-table">
                <thead>
                  <tr>
                    <th className="th-panna">Panna</th>
                    <th className="th-type text-center">Game Type</th>
                    <th className="th-points text-end">Points (₹)</th>
                    <th className="th-action" aria-label="Delete"></th>
                  </tr>
                </thead>
                <tbody>
                  {bids.map((bid) => (
                    <tr key={bid.id}>
                      <td className="td-panna">{bid.panna}</td>
                      <td className="td-type text-center">{bid.gameType}</td>
                      <td className="td-points text-end">{bid.points}</td>
                      <td className="td-action text-end">
                        <button
                          type="button"
                          className="matka-jodi-delete-btn"
                          onClick={() => handleDeleteBid(bid.id)}
                          aria-label={`Delete ${bid.panna}`}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* In-page Action Bar with Bids, Points, and CONTINUE */}
          <div className="matka-bulk-summary-bar">
            <div className="d-flex align-items-center gap-3">
              {/* Bids Counter */}
              <div className="matka-bulk-counter-pill">
                <span className="matka-bulk-counter-val">{totalBids}</span>
                <span className="matka-bulk-counter-label">Bids</span>
              </div>

              {/* Total Points Counter */}
              <div className="matka-bulk-counter-pill">
                <span className="matka-bulk-counter-val">
                  {totalPoints.toLocaleString()}
                </span>
                <span className="matka-bulk-counter-label">Total Points</span>
              </div>
            </div>

            {/* Continue Action Button */}
            <button
              type="button"
              className="matka-bulk-continue-btn"
              onClick={handleContinue}
              disabled={totalBids === 0}
            >
              CONTINUE
            </button>
          </div>
        </div>
      </main>

      {notice && (
        <div className="matka-sangam-popup" role="dialog" aria-modal="true">
          <div className="matka-sangam-popup-box">
            <p>{notice}</p>
            <button type="button" onClick={() => setNotice("")}>
              OK
            </button>
          </div>
        </div>
      )}

      {betModals}

      {/* Matka Footer */}
      <MatkaFooter
        onSelectTab={onSelectTab}
        onBackToMarkets={onBackToMarkets}
      />
    </div>
  );
}
